import {
	portalTangent,
	portalTangentDistance,
	portalTransformAngle,
	signedPortalDistance,
	transformPortalPoint,
	transformPortalVector,
} from './portalMath.js';
import {
	bodyVelocityAtPoint,
	getFixturePortalProjection,
} from './portalCollision.js';

// Travellers and ghosts pair through two collision bits no level object uses.
// A ghost only ever collides with a fixture that carries the traveller bit or
// with another ghost, so the rest of the world never even sees a ghost in the
// broad phase.
export const PORTAL_GHOST_CATEGORY = 0x0200;
export const PORTAL_TRAVELLER_CATEGORY = 0x0400;

const GHOST_REGION_MARGIN = 3;
const GHOST_PLANE_TOLERANCE = 0.05;
const SIDE_IDLE_FRAMES = 30;
// Above this speed a prop's copy becomes a Box2D bullet for the coming step.
const BULLET_COPY_SPEED = 4;

// A copy on a portal side either stands for exit-room geometry (a "ghost"
// behind the entrance) or for a travelling island that is emerging there (a
// "shadow" around the exit). Only shadows may touch ordinary bodies.
export const portalCopyIsShadow = copyBody => {
	const candidate = copyBody?._portalGhostOf?._portalTransitCandidate;
	return Boolean(candidate) && candidate.target === copyBody._portalGhostSide &&
		candidate.source === copyBody._portalGhostOther;
};

const readVec = vector => ({x: vector.get_x(), y: vector.get_y()});

/**
 * Portal physics in a single Box2D world.
 *
 * A travelling island stays a normal set of primary bodies. The part of it
 * that has crossed the entrance plane is, in reality, emerging from the exit,
 * so the geometry in front of the exit is cloned behind the entrance as
 * "ghost" fixtures, mapped through the inverse portal transform. The island
 * then collides with those ghosts in the same solver step as everything else:
 * warm starting, joints, suspension and continuous collision all see one
 * consistent world. Contact points are clipped by the entrance plane in
 * PreSolve (see PortalBridge), so near geometry only acts in front of the
 * plane and ghosts only act behind it.
 *
 * The exit room gets the mirror image of that: the island's "shadow" is a
 * mass-correct copy at the exit, with the island's joints, that the exit
 * room's bodies collide with on its emerged part. Its remainder behind the
 * exit rests on ghosts of the entrance-room geometry, so a plank threaded
 * through a portal carries a bike on its emerged half exactly as the real
 * plank carries it. Copies are re-synchronised from their real bodies every
 * frame and never feed anything back: each real body is solved exactly
 * against copies of the others, on its own side.
 *
 * Ghosts of one side collide with each other unclipped (a ghost crate rests
 * on the ghost floor), so a traveller pressing on a copy meets real support.
 */
export class PortalGhostWorld {
	constructor(Box2D, world, options = {}) {
		this.Box2D = Box2D;
		this.world = world;
		this.pointerOf = object => Box2D.getPointer(object);
		this.nullPointer = Box2D.getPointer(Box2D.NULL);
		this.transits = new Set();
		this.sides = new Map();
		this.regionMargin = options.regionMargin ?? GHOST_REGION_MARGIN;
		// Fast prop copies get continuous collision; see syncGhostState.
		this.bulletCopies = options.bulletCopies ?? true;
		this.retiredBodies = [];
		this.destroyed = false;
		this.queryCallback = new Box2D.JSQueryCallback();
		this.queryFixtures = [];
		this.queryCallback.ReportFixture = fixturePointer => {
			const fixture = typeof fixturePointer === 'number' ?
				Box2D.wrapPointer(fixturePointer, Box2D.b2Fixture) : fixturePointer;
			this.queryFixtures.push(fixture);
			return true;
		};
		this.queryAABB = new Box2D.b2AABB();
	}

	isNull(object) {
		return !object || this.pointerOf(object) === this.nullPointer;
	}

	bodyKey(body) {
		return this.pointerOf(body);
	}

	// Emscripten may hand back a recycled wrapper for a new native body. Never
	// let bookkeeping from an earlier level object or portal body leak into it.
	// Keep __emscripten_pool: the editor's CreateBody only cleans wrappers with that mark.
	scrubBodyWrapper(body) {
		for(const key of [
			'destroyed', 'mySprite', 'myTexture', 'class',
			'_portalTransitCandidate', '_portalCooldownUntilFrame', '_portalCooldownWorld',
			'_portalGhostOf', '_portalGhostSide', '_portalGhostOther', '_portalShadowOf',
			'isPortalGhost', 'isPortalShadow', 'isPortalSurface', 'portalEndpoint',
			'isVehiclePart', 'isFlesh', 'mainCharacter', 'snapped', 'isRopeEnd',
			'recentlyImpactedBodies', 'lockPositionForOneFrame', '_pixiTransformSynced',
		]) delete body[key];
	}

	scrubFixtureWrapper(fixture) {
		delete fixture._portalGhostOf;
		delete fixture._portalShadowOf;
		delete fixture._portalBackstop;
	}

	// ---------------------------------------------------------------------
	// Portal mapping

	endpointState(endpoint) {
		return {
			position: readVec(endpoint.body.GetPosition()),
			angle: endpoint.body.GetAngle(),
		};
	}

	mappedState(body, source, target) {
		const sourceState = this.endpointState(source);
		const targetState = this.endpointState(target);
		const angleDelta = portalTransformAngle(sourceState.angle, targetState.angle);
		const position = readVec(body.GetPosition());
		const mappedPosition = transformPortalPoint(
			position,
			sourceState.position,
			sourceState.angle,
			targetState.position,
			targetState.angle,
		);
		// A portal mounted on a moving body carries that body's velocity. Map the
		// traveller's motion relative to the entrance and add the exit's own
		// motion, so an object emerging onto a sliding host neither lags behind
		// it nor overshoots it. Stationary endpoints contribute nothing here.
		const sourceVelocity = bodyVelocityAtPoint(source.body, position);
		const targetVelocity = bodyVelocityAtPoint(target.body, mappedPosition);
		const velocity = readVec(body.GetLinearVelocity());
		const relativeVelocity = transformPortalVector(
			{x: velocity.x - sourceVelocity.x, y: velocity.y - sourceVelocity.y},
			sourceState.angle,
			targetState.angle,
		);
		return {
			position: mappedPosition,
			angle: body.GetAngle() + angleDelta,
			linearVelocity: {
				x: relativeVelocity.x + targetVelocity.x,
				y: relativeVelocity.y + targetVelocity.y,
			},
			angularVelocity: body.GetAngularVelocity() -
				source.body.GetAngularVelocity() + target.body.GetAngularVelocity(),
			angleDelta,
		};
	}

	mapPoint(point, source, target) {
		const sourceState = this.endpointState(source);
		const targetState = this.endpointState(target);
		return transformPortalPoint(point, sourceState.position, sourceState.angle, targetState.position, targetState.angle);
	}

	// ---------------------------------------------------------------------
	// Island discovery

	castJoint(joint) {
		const {Box2D} = this;
		switch(joint.GetType()) {
			case Box2D.e_revoluteJoint:
				return Box2D.castObject(joint, Box2D.b2RevoluteJoint);
			case Box2D.e_prismaticJoint:
				return Box2D.castObject(joint, Box2D.b2PrismaticJoint);
			case Box2D.e_distanceJoint:
				return Box2D.castObject(joint, Box2D.b2DistanceJoint);
			case Box2D.e_pulleyJoint:
				return Box2D.castObject(joint, Box2D.b2PulleyJoint);
			case Box2D.e_mouseJoint:
				return Box2D.castObject(joint, Box2D.b2MouseJoint);
			case Box2D.e_wheelJoint:
				return Box2D.castObject(joint, Box2D.b2WheelJoint);
			case Box2D.e_weldJoint:
				return Box2D.castObject(joint, Box2D.b2WeldJoint);
			case Box2D.e_frictionJoint:
				return Box2D.castObject(joint, Box2D.b2FrictionJoint);
			case Box2D.e_motorJoint:
				return Box2D.castObject(joint, Box2D.b2MotorJoint);
			default:
				return joint;
		}
	}

	collectIsland(seedBody) {
		const {Box2D} = this;
		const bodies = [];
		const bodyKeys = new Set();
		const joints = [];
		const jointKeys = new Set();
		const externalJoints = [];
		const queue = [seedBody];

		while(queue.length) {
			const body = queue.shift();
			const key = this.bodyKey(body);
			if(bodyKeys.has(key)) continue;
			bodyKeys.add(key);
			bodies.push(body);

			for(let edge = body.GetJointList(); !this.isNull(edge); edge = edge.get_next()) {
				const joint = this.castJoint(edge.joint);
				const jointKey = this.pointerOf(joint);
				if(!jointKeys.has(jointKey)) {
					jointKeys.add(jointKey);
					joints.push(joint);
				}
				const otherBody = joint.GetBodyA() === body ? joint.GetBodyB() : joint.GetBodyA();
				if(!otherBody.IsEnabled()) continue;
				if(otherBody.GetType() === Box2D.b2_staticBody || otherBody.isPortalSurface) {
					externalJoints.push(joint);
					continue;
				}
				if(!bodyKeys.has(this.bodyKey(otherBody))) queue.push(otherBody);
			}
		}

		const internalJoints = joints.filter(joint =>
			bodyKeys.has(this.bodyKey(joint.GetBodyA())) &&
			bodyKeys.has(this.bodyKey(joint.GetBodyB()))
		);

		return {bodies, bodyKeys, joints: internalJoints, externalJoints};
	}

	jointAnchoredBehindPortal(sourceJoint, endpoint) {
		const joint = this.castJoint(sourceJoint);
		const bodyA = joint.GetBodyA();
		const anchorOnA = bodyA.GetType() === this.Box2D.b2_staticBody || bodyA.isPortalSurface;
		const anchor = readVec(anchorOnA ? joint.GetAnchorA() : joint.GetAnchorB());
		return signedPortalDistance(
			anchor,
			readVec(endpoint.body.GetPosition()),
			endpoint.body.GetAngle(),
		) < -0.01;
	}

	// ---------------------------------------------------------------------
	// Structure signatures (a flip mirrors vertices and anchors in place)

	fixtureStructureSignature(fixture) {
		const {Box2D} = this;
		const shape = fixture.GetShape();
		const type = shape.GetType();
		const parts = [this.pointerOf(fixture), type];
		const round = value => Math.round(value * 10000);
		if(type === Box2D.b2Shape.e_circle) {
			const circle = Box2D.castObject(shape, Box2D.b2CircleShape);
			const center = circle.get_m_p();
			parts.push(round(circle.get_m_radius()), round(center.get_x()), round(center.get_y()));
		} else if(type === Box2D.b2Shape.e_polygon) {
			const polygon = Box2D.castObject(shape, Box2D.b2PolygonShape);
			const count = polygon.get_m_count();
			parts.push(count);
			for(let index = 0; index < count; index++) {
				const vertex = polygon.get_m_vertices(index);
				parts.push(round(vertex.get_x()), round(vertex.get_y()));
			}
		} else if(type === Box2D.b2Shape.e_edge) {
			const edge = Box2D.castObject(shape, Box2D.b2EdgeShape);
			const vertex1 = edge.get_m_vertex1();
			const vertex2 = edge.get_m_vertex2();
			parts.push(
				round(vertex1.get_x()), round(vertex1.get_y()),
				round(vertex2.get_x()), round(vertex2.get_y()),
			);
		}
		return parts.join(',');
	}

	jointStructureSignature(sourceJoint) {
		const joint = this.castJoint(sourceJoint);
		const round = value => Math.round(value * 10000);
		const parts = [
			this.pointerOf(joint),
			joint.GetType(),
			this.pointerOf(joint.GetBodyA()),
			this.pointerOf(joint.GetBodyB()),
		];
		if(typeof joint.GetLocalAnchorA === 'function') {
			const anchorA = joint.GetLocalAnchorA();
			const anchorB = joint.GetLocalAnchorB();
			parts.push(
				round(anchorA.get_x()), round(anchorA.get_y()),
				round(anchorB.get_x()), round(anchorB.get_y()),
			);
		}
		if(typeof joint.GetLocalAxisA === 'function') {
			const axis = joint.GetLocalAxisA();
			parts.push(round(axis.get_x()), round(axis.get_y()));
		}
		if(typeof joint.GetReferenceAngle === 'function') {
			parts.push(round(joint.GetReferenceAngle()));
		}
		return parts.join(',');
	}

	bodyStructureSignature(body) {
		const parts = [`b${this.bodyKey(body)}`];
		// A rope hook rebuilds its collider every frame and never collides in
		// either frame, so its fixtures are not part of the structure.
		if(!body.isRopeEnd) {
			for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
				parts.push(this.fixtureStructureSignature(fixture));
			}
		}
		return parts.join('|');
	}

	transitSignature(candidate) {
		const parts = [];
		for(const body of candidate.bodies) {
			// A disabled body (an expired gib) has left the island like a destroyed one.
			if(body.destroyed || !body.IsEnabled()) {
				parts.push('destroyed');
				continue;
			}
			parts.push(this.bodyStructureSignature(body));
			for(let edge = body.GetJointList(); !this.isNull(edge); edge = edge.get_next()) {
				parts.push(this.jointStructureSignature(edge.joint));
			}
		}
		return parts.join('|');
	}

	transitStructureChanged(candidate) {
		return candidate.structureSignature !== this.transitSignature(candidate);
	}

	// ---------------------------------------------------------------------
	// Transit lifecycle

	createTransit(source, target, seedBody, frame = 0) {
		const {Box2D} = this;
		if(this.destroyed) return null;
		if(!seedBody || seedBody.destroyed || seedBody.isPortalSurface ||
			seedBody.isPortalGhost || seedBody.isPortalShadow) return null;
		if(seedBody.GetType() === Box2D.b2_staticBody) return null;
		if(seedBody._portalCooldownWorld &&
			seedBody._portalCooldownWorld !== this.world) {
			delete seedBody._portalCooldownUntilFrame;
			delete seedBody._portalCooldownWorld;
		}
		if(seedBody._portalCooldownUntilFrame > frame) return null;
		const existing = seedBody._portalTransitCandidate;
		if(existing && this.transits.has(existing)) return existing;
		if(existing) delete seedBody._portalTransitCandidate;

		const island = this.collectIsland(seedBody);
		// A joint anchored to static world geometry cannot move through a portal
		// without changing what that joint means, so an island anchored on this
		// side waits until the anchor is released. An anchor already behind the
		// cross-line is a rope hooked through the portal: it really sits on the
		// far side, and the world keeps enforcing it.
		if(island.externalJoints.some(joint => !this.jointAnchoredBehindPortal(joint, source))) {
			return null;
		}
		for(const body of island.bodies) {
			const candidate = body._portalTransitCandidate;
			if(candidate && this.transits.has(candidate)) return null;
			if(candidate) delete body._portalTransitCandidate;
		}

		const candidate = {
			ghostWorld: this,
			source,
			target,
			seedBody,
			bodies: [],
			bodyKeys: new Set(),
			joints: [],
			bodyMap: new Map(),
			structureSignature: '',
			createdFrame: frame,
			ownershipTransferCount: 0,
			ownershipTransferArmed: true,
			lastOwnershipTransferFrame: -Infinity,
			endedContact: false,
			sourceSensorFixtureKeys: new Set(),
			admittedFixtureKeys: new Set(),
			restingOnHost: false,
			shadowsPending: true,
		};
		this.populateTransit(candidate, island);
		this.transits.add(candidate);
		return candidate;
	}

	// Bookkeeping only: this can run inside a Box2D callback, where the world
	// is locked. Shadow and ghost bodies are created by sync() after the step.
	populateTransit(candidate, island) {
		candidate.bodies = island.bodies;
		candidate.bodyKeys = island.bodyKeys;
		candidate.joints = island.joints;
		for(const body of island.bodies) {
			body._portalTransitCandidate = candidate;
			this.tagTravellerFixtures(body);
		}
		candidate.structureSignature = this.transitSignature(candidate);
		candidate.bodyMap.clear();
		candidate.shadowsPending = true;
	}

	tagTravellerFixtures(body) {
		if(body.destroyed) return;
		for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
			const filter = fixture.GetFilterData();
			const category = filter.get_categoryBits();
			const mask = filter.get_maskBits();
			if((category & PORTAL_TRAVELLER_CATEGORY) && (mask & PORTAL_GHOST_CATEGORY)) continue;
			filter.set_categoryBits(category | PORTAL_TRAVELLER_CATEGORY);
			filter.set_maskBits(mask | PORTAL_GHOST_CATEGORY);
			fixture.SetFilterData(filter);
		}
	}

	untagTravellerFixtures(body) {
		if(body.destroyed) return;
		for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
			const filter = fixture.GetFilterData();
			const category = filter.get_categoryBits();
			const mask = filter.get_maskBits();
			if(!(category & PORTAL_TRAVELLER_CATEGORY) && !(mask & PORTAL_GHOST_CATEGORY)) continue;
			filter.set_categoryBits(category & ~PORTAL_TRAVELLER_CATEGORY);
			filter.set_maskBits(mask & ~PORTAL_GHOST_CATEGORY);
			fixture.SetFilterData(filter);
		}
	}

	// Bodies join and leave islands (a snapped wheel, a grabbed object). Keep
	// the traveller bits exactly on the live island.
	refreshTravellerTags(candidate) {
		for(const body of candidate.bodies) this.tagTravellerFixtures(body);
	}

	// Recreate a transit's island from its current shapes and joints. Transit
	// bookkeeping (endpoints, ownership, admitted fixtures) survives. Returns
	// false when the island can no longer travel, in which case the caller
	// drops it.
	rebuildTransit(candidate) {
		if(!this.transits.has(candidate)) return false;
		const seedBody = [candidate.seedBody, ...candidate.bodies].find(body =>
			body && !body.destroyed && body.IsEnabled() && !body.isPortalSurface
		);
		if(!seedBody) return false;

		const island = this.collectIsland(seedBody);
		for(const body of island.bodies) {
			const other = body._portalTransitCandidate;
			if(other && other !== candidate && this.transits.has(other)) return false;
		}
		for(const body of candidate.bodies) {
			if(!island.bodyKeys.has(this.bodyKey(body)) &&
				body._portalTransitCandidate === candidate) {
				delete body._portalTransitCandidate;
				this.untagTravellerFixtures(body);
			}
		}
		candidate.seedBody = seedBody;
		this.populateTransit(candidate, island);

		const liveFixtureKeys = new Set();
		for(const body of island.bodies) {
			for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
				liveFixtureKeys.add(this.pointerOf(fixture));
			}
		}
		for(const keys of [candidate.admittedFixtureKeys, candidate.sourceSensorFixtureKeys]) {
			for(const key of [...keys]) if(!liveFixtureKeys.has(key)) keys.delete(key);
		}
		return true;
	}

	applyDestinationStates(candidate, frame) {
		const {Box2D} = this;
		const transferred = [];
		// Primary physics has already combined the island's own motion and its
		// ghost contacts. Transform that authoritative state body by body so the
		// articulated pose stays exactly continuous through the frame change.
		const states = candidate.bodies
			.filter(body => !body.destroyed)
			.map(body => ({body, state: this.mappedState(body, candidate.source, candidate.target)}));
		for(const {body, state} of states) {
			const position = new Box2D.b2Vec2(state.position.x, state.position.y);
			const velocity = new Box2D.b2Vec2(state.linearVelocity.x, state.linearVelocity.y);
			body.SetTransform(position, state.angle);
			body.SetLinearVelocity(velocity);
			body.SetAngularVelocity(state.angularVelocity);
			body.SetAwake(true);
			body._portalCooldownUntilFrame = frame + 8;
			body._portalCooldownWorld = this.world;
			transferred.push(body);
			Box2D.destroy(velocity);
			Box2D.destroy(position);
		}
		return transferred;
	}

	// Move the authoritative island to the destination frame. The shadow
	// bodies then represent the remainder still hanging at the old entrance.
	rebaseTransit(candidate, frame) {
		if(!this.transits.has(candidate)) return [];
		const transferred = this.applyDestinationStates(candidate, frame);
		const oldSource = candidate.source;
		candidate.source = candidate.target;
		candidate.target = oldSource;
		candidate.ownershipTransferCount = (candidate.ownershipTransferCount || 0) + 1;
		candidate.ownershipTransferArmed = false;
		candidate.lastOwnershipTransferFrame = frame;
		candidate.sourceSensorFixtureKeys.clear();
		candidate.admittedFixtureKeys.clear();
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			for(let fixture = body.GetFixtureList();
				!this.isNull(fixture);
				fixture = fixture.GetNext()) {
				candidate.admittedFixtureKeys.add(this.pointerOf(fixture));
			}
		}
		candidate.endedContact = false;
		return transferred;
	}

	removeTransit(candidate) {
		if(!this.transits.has(candidate)) return;
		for(const body of candidate.bodies) {
			if(body._portalTransitCandidate === candidate) delete body._portalTransitCandidate;
			this.untagTravellerFixtures(body);
		}
		candidate.bodyMap.clear();
		candidate.sourceSensorFixtureKeys.clear();
		candidate.admittedFixtureKeys.clear();
		this.transits.delete(candidate);
	}

	// ---------------------------------------------------------------------
	// Island measurements (all in the island's current canonical frame)

	getIslandCenter(candidate) {
		let x = 0;
		let y = 0;
		let totalWeight = 0;
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			const mass = Math.max(body.GetMass(), 0.001);
			const position = body.GetWorldCenter();
			x += position.get_x() * mass;
			y += position.get_y() * mass;
			totalWeight += mass;
		}
		return totalWeight ? {x: x / totalWeight, y: y / totalWeight} : readVec(candidate.seedBody.GetPosition());
	}

	// Whether a body came in through the opening. A body that passes the plane
	// outside the opening has gone round the portal and is still in this room.
	// Sensor-only attachments such as eyes follow the island.
	bodyEntered(body, candidate) {
		let solid = false;
		for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
			if(fixture.IsSensor()) continue;
			if(candidate.admittedFixtureKeys.has(this.pointerOf(fixture))) return true;
			solid = true;
		}
		return !solid;
	}

	// How far the island's mass centre is in front of the entrance plane, with
	// bodies that went round the portal counted as being as far in front.
	getIslandEntryDistance(candidate) {
		const sourcePosition = readVec(candidate.source.body.GetPosition());
		const sourceAngle = candidate.source.body.GetAngle();
		let distance = 0;
		let totalMass = 0;
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			const mass = Math.max(body.GetMass(), 0.001);
			const own = signedPortalDistance(readVec(body.GetWorldCenter()), sourcePosition, sourceAngle);
			distance += (this.bodyEntered(body, candidate) ? own : Math.abs(own)) * mass;
			totalMass += mass;
		}
		return totalMass ? distance / totalMass : 0;
	}

	// Which side of the entrance plane a body lies wholly on, if any.
	bodySide(body, endpoint) {
		let front = true;
		let behind = true;
		let solid = false;
		for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
			if(fixture.IsSensor()) continue;
			solid = true;
			const {normal} = getFixturePortalProjection(this.Box2D, fixture, endpoint);
			if(normal.min < -0.01) front = false;
			if(normal.max > 0.01) behind = false;
		}
		if(!solid) return null;
		return front ? 'front' : behind ? 'behind' : null;
	}

	// A joint between a part in front of the entrance and one that came through
	// it is a pin through the opening. Joints have no shape for the lips to stop,
	// so where such a pin crosses the plane is kept between the lips: the island
	// is moved back along the plane and stops sliding past the end.
	holdJointsInOpening(candidate) {
		const {Box2D} = this;
		const source = candidate.source;
		const position = readVec(source.body.GetPosition());
		const angle = source.body.GetAngle();
		const limit = source.apertureHalfLength;
		let shift = 0;
		for(const sourceJoint of candidate.joints) {
			const joint = this.castJoint(sourceJoint);
			const bodyA = joint.GetBodyA();
			const bodyB = joint.GetBodyB();
			if(bodyA.destroyed || bodyB.destroyed) continue;
			const sideA = this.bodySide(bodyA, source);
			const sideB = this.bodySide(bodyB, source);
			const through = (sideA === 'front' && sideB === 'behind' && this.bodyEntered(bodyB, candidate)) ||
				(sideB === 'front' && sideA === 'behind' && this.bodyEntered(bodyA, candidate));
			if(!through) continue;
			const a = readVec(joint.GetAnchorA());
			const b = readVec(joint.GetAnchorB());
			const da = signedPortalDistance(a, position, angle);
			const db = signedPortalDistance(b, position, angle);
			const k = da * db < 0 ? da / (da - db) : 0.5;
			const t = portalTangentDistance({x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k}, position, angle);
			const over = Math.abs(t) - limit;
			if(over > Math.abs(shift)) shift = Math.sign(t) * over;
		}
		if(!shift) return;
		const tangent = portalTangent(angle);
		for(const body of candidate.bodies) {
			if(body.destroyed || !body.IsEnabled()) continue;
			const position = readVec(body.GetPosition());
			const velocity = readVec(body.GetLinearVelocity());
			const moved = new Box2D.b2Vec2(position.x - tangent.x * shift, position.y - tangent.y * shift);
			body.SetTransform(moved, body.GetAngle());
			Box2D.destroy(moved);
			const along = velocity.x * tangent.x + velocity.y * tangent.y;
			if(along * shift > 0) {
				const stopped = new Box2D.b2Vec2(velocity.x - tangent.x * along, velocity.y - tangent.y * along);
				body.SetLinearVelocity(stopped);
				Box2D.destroy(stopped);
			}
		}
	}

	// Share of the island's mass whose centre lies in front of the entrance
	// plane: the part that has emerged on the authoritative side.
	getIslandFrontMassFraction(candidate) {
		const sourcePosition = readVec(candidate.source.body.GetPosition());
		const sourceAngle = candidate.source.body.GetAngle();
		let frontMass = 0;
		let totalMass = 0;
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			const mass = Math.max(body.GetMass(), 0.001);
			totalMass += mass;
			if(signedPortalDistance(readVec(body.GetWorldCenter()), sourcePosition, sourceAngle) >= 0) {
				frontMass += mass;
			}
		}
		return totalMass ? frontMass / totalMass : 0;
	}

	// Which sides of the entrance plane hold a body matching the predicate.
	getIslandSidePresence(candidate, predicate) {
		const sourcePosition = readVec(candidate.source.body.GetPosition());
		const sourceAngle = candidate.source.body.GetAngle();
		const presence = {front: false, back: false};
		for(const body of candidate.bodies) {
			if(body.destroyed || !predicate(body)) continue;
			const inFront = signedPortalDistance(
				readVec(body.GetWorldCenter()),
				sourcePosition,
				sourceAngle,
			) >= 0;
			if(inFront) presence.front = true;
			else presence.back = true;
		}
		return presence;
	}

	// Only bodies that came in through the opening keep a transit open.
	getIslandPortalDistanceRange(candidate) {
		let min = Infinity;
		let max = -Infinity;
		for(const body of candidate.bodies) {
			if(body.destroyed || !this.bodyEntered(body, candidate)) continue;
			let includedFixture = false;
			for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
				includedFixture = true;
				const projection = getFixturePortalProjection(this.Box2D, fixture, candidate.source);
				min = Math.min(min, projection.normal.min);
				max = Math.max(max, projection.normal.max);
			}
			if(!includedFixture) {
				const distance = signedPortalDistance(
					readVec(body.GetWorldCenter()),
					readVec(candidate.source.body.GetPosition()),
					candidate.source.body.GetAngle(),
				);
				min = Math.min(min, distance);
				max = Math.max(max, distance);
			}
		}
		return Number.isFinite(min) ? {min, max} : {min: 0, max: 0};
	}

	// World-space bounds of every fixture in the island.
	getIslandBounds(candidate) {
		const bounds = {minX: Infinity, minY: Infinity, maxX: -Infinity, maxY: -Infinity};
		for(const body of candidate.bodies) {
			// Only enabled bodies have broadphase boxes.
			if(body.destroyed || !body.IsEnabled()) continue;
			let includedFixture = false;
			for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
				const shape = fixture.GetShape();
				for(let child = 0; child < shape.GetChildCount(); child++) {
					const aabb = fixture.GetAABB(child);
					const lower = aabb.get_lowerBound();
					const upper = aabb.get_upperBound();
					bounds.minX = Math.min(bounds.minX, lower.get_x());
					bounds.minY = Math.min(bounds.minY, lower.get_y());
					bounds.maxX = Math.max(bounds.maxX, upper.get_x());
					bounds.maxY = Math.max(bounds.maxY, upper.get_y());
					includedFixture = true;
				}
			}
			if(!includedFixture) {
				const center = readVec(body.GetWorldCenter());
				bounds.minX = Math.min(bounds.minX, center.x);
				bounds.minY = Math.min(bounds.minY, center.y);
				bounds.maxX = Math.max(bounds.maxX, center.x);
				bounds.maxY = Math.max(bounds.maxY, center.y);
			}
		}
		return Number.isFinite(bounds.minX) ? bounds : null;
	}

	// ---------------------------------------------------------------------
	// Shape copies

	createShapeCopy(fixture) {
		const {Box2D} = this;
		const sourceShape = fixture.GetShape();
		const type = sourceShape.GetType();

		if(type === Box2D.b2Shape.e_circle) {
			const sourceCircle = Box2D.castObject(sourceShape, Box2D.b2CircleShape);
			const circle = new Box2D.b2CircleShape();
			circle.set_m_radius(sourceCircle.get_m_radius());
			const center = sourceCircle.get_m_p();
			const centerCopy = new Box2D.b2Vec2(center.get_x(), center.get_y());
			circle.set_m_p(centerCopy);
			Box2D.destroy(centerCopy);
			return circle;
		}

		if(type === Box2D.b2Shape.e_polygon) {
			const sourcePolygon = Box2D.castObject(sourceShape, Box2D.b2PolygonShape);
			const vertices = [];
			for(let index = 0; index < sourcePolygon.get_m_count(); index++) {
				vertices.push(readVec(sourcePolygon.get_m_vertices(index)));
			}
			const polygon = new Box2D.b2PolygonShape();
			const [vertexPointer, destroyVertices] = Box2D.pointsToVec2Array(vertices);
			polygon.Set(vertexPointer, vertices.length);
			destroyVertices();
			return polygon;
		}

		if(type === Box2D.b2Shape.e_edge) {
			const sourceEdge = Box2D.castObject(sourceShape, Box2D.b2EdgeShape);
			const edge = new Box2D.b2EdgeShape();
			const v1 = sourceEdge.get_m_vertex1();
			const v2 = sourceEdge.get_m_vertex2();
			const vertex1 = new Box2D.b2Vec2(v1.get_x(), v1.get_y());
			const vertex2 = new Box2D.b2Vec2(v2.get_x(), v2.get_y());
			if(sourceEdge.get_m_oneSided()) {
				const v0 = sourceEdge.get_m_vertex0();
				const v3 = sourceEdge.get_m_vertex3();
				const vertex0 = new Box2D.b2Vec2(v0.get_x(), v0.get_y());
				const vertex3 = new Box2D.b2Vec2(v3.get_x(), v3.get_y());
				edge.SetOneSided(vertex0, vertex1, vertex2, vertex3);
				Box2D.destroy(vertex3);
				Box2D.destroy(vertex0);
			} else {
				edge.SetTwoSided(vertex1, vertex2);
			}
			Box2D.destroy(vertex2);
			Box2D.destroy(vertex1);
			return edge;
		}

		if(type === Box2D.b2Shape.e_chain) {
			const sourceChain = Box2D.castObject(sourceShape, Box2D.b2ChainShape);
			const count = sourceChain.get_m_count();
			const vertexArray = Box2D.reifyArray(
				this.pointerOf(sourceChain.get_m_vertices()),
				count,
				8,
				Box2D.b2Vec2,
			);
			const vertices = vertexArray.map(readVec);
			const chain = new Box2D.b2ChainShape();
			const isLoop = vertices.length > 2 &&
				Math.abs(vertices[0].x - vertices[vertices.length - 1].x) < 0.000001 &&
				Math.abs(vertices[0].y - vertices[vertices.length - 1].y) < 0.000001;
			const chainVertices = isLoop ? vertices.slice(0, -1) : vertices;
			const [vertexPointer, destroyVertices] = Box2D.pointsToVec2Array(chainVertices);
			if(isLoop) {
				chain.CreateLoop(vertexPointer, chainVertices.length);
			} else {
				const previous = sourceChain.get_m_prevVertex();
				const next = sourceChain.get_m_nextVertex();
				chain.CreateChain(vertexPointer, chainVertices.length, previous, next);
			}
			destroyVertices();
			return chain;
		}

		return null;
	}

	createBodyLike(sourceBody, type, position, angle, {gravityScale}) {
		const {Box2D} = this;
		const bodyDef = new Box2D.b2BodyDef();
		bodyDef.set_type(type);
		const positionVector = new Box2D.b2Vec2(position.x, position.y);
		bodyDef.set_position(positionVector);
		bodyDef.set_angle(angle);
		bodyDef.set_linearDamping(0);
		bodyDef.set_angularDamping(0);
		bodyDef.set_allowSleep(true);
		bodyDef.set_awake(true);
		bodyDef.set_fixedRotation(sourceBody.IsFixedRotation());
		bodyDef.set_bullet(false);
		bodyDef.set_gravityScale(gravityScale);
		const body = this.world.CreateBody(bodyDef);
		this.scrubBodyWrapper(body);
		Box2D.destroy(positionVector);
		Box2D.destroy(bodyDef);
		return body;
	}

	copyFixture(sourceFixture, targetBody, {shadow}) {
		const {Box2D} = this;
		const shape = this.createShapeCopy(sourceFixture);
		if(!shape) return null;
		const fixtureDef = new Box2D.b2FixtureDef();
		fixtureDef.set_shape(shape);
		fixtureDef.set_density(sourceFixture.GetDensity());
		fixtureDef.set_friction(sourceFixture.GetFriction());
		fixtureDef.set_restitution(sourceFixture.GetRestitution());
		fixtureDef.set_restitutionThreshold(sourceFixture.GetRestitutionThreshold());
		fixtureDef.set_isSensor(sourceFixture.IsSensor());
		const filter = fixtureDef.get_filter();
		const original = sourceFixture.GetFilterData();
		// Ghosts pair with travellers and with each other: a ghost plank rests on
		// the ghost floor exactly as the real plank rests on the real floor, so a
		// traveller pressing on it meets real support instead of a free body. A
		// shadow additionally keeps its real body's pairing with the room, so the
		// exit room can stand on, hit and trigger the emerged part.
		filter.set_categoryBits(PORTAL_GHOST_CATEGORY |
			(shadow ? original.get_categoryBits() & ~PORTAL_TRAVELLER_CATEGORY : 0));
		filter.set_maskBits(PORTAL_TRAVELLER_CATEGORY | PORTAL_GHOST_CATEGORY |
			(shadow ? original.get_maskBits() : 0));
		filter.set_groupIndex(0);
		const copy = targetBody.CreateFixture(fixtureDef);
		this.scrubFixtureWrapper(copy);
		Box2D.destroy(fixtureDef);
		Box2D.destroy(shape);
		return copy;
	}

	destroyOwnedBody(body) {
		// The editor's level reset destroys every body in the world, flagging
		// them first; never destroy such a body a second time.
		if(!body || body.destroyed) return;
		// box2d-wasm hands the same JavaScript wrapper to whatever body or
		// fixture is later allocated at a freed address, and the editor only
		// scrubs the wrappers of bodies it destroyed itself. Anything left on a
		// copy's wrappers would resurface on a wall of the next level: a stale
		// ghost flag makes rays and contacts ignore it, a stale destroyed mark
		// makes it refuse a portal. Scrub the fixtures now; the body keeps its
		// destroyed mark until this pass ends, then is scrubbed too.
		for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
			this.scrubFixtureWrapper(fixture);
		}
		// Scrub the flags before the address can be reused: a copy created later
		// in this same pass may receive this very wrapper and set its own.
		this.scrubBodyWrapper(body);
		body.destroyed = true;
		this.world.DestroyBody(body);
		this.retiredBodies.push(body);
	}

	// Once a teardown pass is over nothing refers to a retired copy by its
	// wrapper, so the destroyed mark can go too. A copy created in the same
	// pass at a reused address already had the mark scrubbed at creation.
	flushRetiredBodies() {
		if(!this.retiredBodies.length) return;
		for(const body of this.retiredBodies) delete body.destroyed;
		this.retiredBodies.length = 0;
	}

	// ---------------------------------------------------------------------
	// Ghost sides

	sideFor(endpoint) {
		return this.sides.get(endpoint) || null;
	}

	ensureSide(source, target, frame) {
		let side = this.sides.get(source);
		if(!side) {
			side = {
				endpoint: source,
				other: target,
				ghosts: new Map(),
				joints: new Map(),
				lastPose: null,
				idleSinceFrame: null,
			};
			this.sides.set(source, side);
		}
		side.other = target;
		side.idleSinceFrame = null;
		return side;
	}

	// Islands entering through this side: their crossed parts sit behind it and
	// need the far room's geometry as ghosts.
	sideTransits(side) {
		return [...this.transits].filter(candidate => candidate.source === side.endpoint);
	}

	// Islands emerging from this side: they need a shadow here, supported by
	// ghosts of what their real bodies rest on in the other room.
	sideShadowTransits(side) {
		return [...this.transits].filter(candidate => candidate.target === side.endpoint);
	}

	islandSpeed(candidate) {
		let speed = 0;
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			const velocity = body.GetLinearVelocity();
			speed = Math.max(speed, Math.hypot(velocity.get_x(), velocity.get_y()));
		}
		return speed;
	}

	// Region of the other room whose geometry this side needs as ghosts: where
	// entering islands' images reach, and where emerging islands really are.
	// Padded so geometry entering next frame is ready.
	bodySpeed(body) {
		if(!body || body.destroyed) return 0;
		const velocity = body.GetLinearVelocity();
		return Math.hypot(velocity.get_x(), velocity.get_y());
	}

	sideRegion(side, transits, shadowTransits) {
		let region = null;
		const include = (corner, margin) => {
			if(!region) region = {minX: corner.x, minY: corner.y, maxX: corner.x, maxY: corner.y};
			region.minX = Math.min(region.minX, corner.x - margin);
			region.minY = Math.min(region.minY, corner.y - margin);
			region.maxX = Math.max(region.maxX, corner.x + margin);
			region.maxY = Math.max(region.maxY, corner.y + margin);
		};
		const cornersOf = bounds => [
			{x: bounds.minX, y: bounds.minY},
			{x: bounds.maxX, y: bounds.minY},
			{x: bounds.maxX, y: bounds.maxY},
			{x: bounds.minX, y: bounds.maxY},
		];
		// A mouth on a moving host drags the mapped region across the room at
		// the host's speed; cover that motion so geometry is copied before a
		// traveller can reach it.
		const endpointSpeed = Math.max(
			this.bodySpeed(side.endpoint.body),
			this.bodySpeed(side.other.body),
		);
		for(const candidate of transits) {
			const bounds = this.getIslandBounds(candidate);
			if(!bounds) continue;
			const margin = this.regionMargin + (this.islandSpeed(candidate) + endpointSpeed) * (2 / 30);
			for(const corner of cornersOf(bounds)) {
				include(this.mapPoint(corner, candidate.source, candidate.target), margin);
			}
		}
		for(const candidate of shadowTransits) {
			const bounds = this.getIslandBounds(candidate);
			if(!bounds) continue;
			const margin = this.regionMargin + (this.islandSpeed(candidate) + endpointSpeed) * (2 / 30);
			for(const corner of cornersOf(bounds)) include(corner, margin);
		}
		return region;
	}

	queryRegion(region) {
		this.queryFixtures.length = 0;
		this.queryAABB.get_lowerBound().Set(region.minX, region.minY);
		this.queryAABB.get_upperBound().Set(region.maxX, region.maxY);
		this.world.QueryAABB(this.queryCallback, this.queryAABB);
		const fixtures = this.queryFixtures.slice();
		this.queryFixtures.length = 0;
		return fixtures;
	}

	ghostableBody(body, side) {
		if(!body || body.destroyed) return false;
		if(body.isPortalSurface || body.isPortalGhost || body.isPortalShadow) return false;
		if(body.isPhysicsCamera || body.isRopeEnd) return false;
		const sprite = body.mySprite;
		if(sprite && (sprite.isCosmeticDebris || sprite.isPhysicsParticleSprite)) return false;
		// An island using this side would meet its own image through the portal.
		const candidate = body._portalTransitCandidate;
		if(candidate && candidate.source === side.endpoint) return false;
		return true;
	}

	// Fixtures with any part at or in front of the exit plane, grouped by body.
	// A dynamic body brings the bodies it is jointed to along: a plank on a
	// pivot needs its pivot, a stack needs every crate. Static joint anchors
	// outside the region become fixtureless ghosts that only carry the joint.
	// Islands emerging here are shadowed whole.
	collectSideGeometry(side, region, shadowTransits) {
		const {Box2D} = this;
		const groups = new Map();
		const target = side.other;
		for(const candidate of shadowTransits) {
			for(const body of candidate.bodies) {
				if(body.destroyed) continue;
				const fixtures = [];
				for(let fixture = body.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
					fixtures.push(fixture);
				}
				groups.set(this.bodyKey(body), {body, fixtures, shadow: true});
			}
		}
		for(const fixture of this.queryRegion(region)) {
			const body = fixture.GetBody();
			if(!this.ghostableBody(body, side)) continue;
			const projection = getFixturePortalProjection(Box2D, fixture, target);
			if(projection.normal.max < -GHOST_PLANE_TOLERANCE) continue;
			const key = this.bodyKey(body);
			let group = groups.get(key);
			if(!group) {
				group = {body, fixtures: [], shadow: false};
				groups.set(key, group);
				// A moving body is copied whole: its copy must carry the real mass
				// and inertia, and its fixture set must not depend on how much of
				// it the region happens to cover this step.
				if(body.GetType() === Box2D.b2_dynamicBody) {
					for(let other = body.GetFixtureList(); !this.isNull(other); other = other.GetNext()) {
						group.fixtures.push(other);
					}
				}
			}
			if(group.shadow || body.GetType() === Box2D.b2_dynamicBody) continue;
			if(!group.fixtures.some(existing => this.pointerOf(existing) === this.pointerOf(fixture))) group.fixtures.push(fixture);
		}
		const queue = [...groups.values()].filter(group => group.body.GetType() === Box2D.b2_dynamicBody);
		const addFixture = (group, fixture) => {
			const pointer = this.pointerOf(fixture);
			if(group.fixtures.some(existing => this.pointerOf(existing) === pointer)) return;
			group.fixtures.push(fixture);
		};
		const includeBody = (otherBody, touchedFixture = null) => {
			const otherKey = this.bodyKey(otherBody);
			let group = groups.get(otherKey);
			if(!group) {
				if(!this.ghostableBody(otherBody, side)) return;
				group = {body: otherBody, fixtures: [], shadow: false};
				groups.set(otherKey, group);
				if(otherBody.GetType() === Box2D.b2_dynamicBody) {
					for(let fixture = otherBody.GetFixtureList(); !this.isNull(fixture); fixture = fixture.GetNext()) {
						group.fixtures.push(fixture);
					}
					queue.push(group);
				}
			}
			// A static neighbour only needs the fixture it actually touches.
			if(touchedFixture && otherBody.GetType() !== Box2D.b2_dynamicBody) addFixture(group, touchedFixture);
		};
		while(queue.length) {
			const {body} = queue.shift();
			for(let edge = body.GetJointList(); !this.isNull(edge); edge = edge.get_next()) {
				const joint = this.castJoint(edge.joint);
				includeBody(joint.GetBodyA() === body ? joint.GetBodyB() : joint.GetBodyA());
			}
			// What the body rests on or leans against must be there in ghost space
			// too, or a traveller's weight would push the ghost into empty space.
			for(let edge = body.GetContactList(); !this.isNull(edge); edge = edge.get_next()) {
				const contact = edge.contact;
				if(!contact.IsTouching() || !contact.IsEnabled()) continue;
				const fixtureA = contact.GetFixtureA();
				const fixtureB = contact.GetFixtureB();
				if(fixtureA.IsSensor() || fixtureB.IsSensor()) continue;
				const otherFixture = this.pointerOf(fixtureA.GetBody()) === this.bodyKey(body) ? fixtureB : fixtureA;
				includeBody(otherFixture.GetBody(), otherFixture);
			}
		}
		return groups;
	}

	ghostSignature(group) {
		const dynamic = group.body.GetType() === this.Box2D.b2_dynamicBody;
		const kind = group.shadow ? 's:' : 'g:';
		if(!group.fixtures.length) return `${kind}anchor`;
		// The region query returns fixtures in broadphase order, which changes
		// from step to step. The signature describes a set: sort it, or a copy
		// is torn down and rebuilt whenever the order flips, losing its contacts
		// (and the wheel standing on it) for a step.
		return kind + group.fixtures.map(fixture => dynamic ?
			this.fixtureStructureSignature(fixture) :
			`${this.pointerOf(fixture)}:${fixture.IsSensor() ? 1 : 0}`
		).sort().join('|');
	}

	// A mouth on a moving host makes the image of the far room move through
	// this one; a static copy cannot carry that motion, so such copies are
	// kinematic and are given the mapped frame velocity every step.
	sideMoves(side) {
		return side.endpoint.body.GetType() !== this.Box2D.b2_staticBody ||
			side.other.body.GetType() !== this.Box2D.b2_staticBody;
	}

	createGhost(side, group, signature) {
		const {Box2D} = this;
		const body = group.body;
		const type = body.GetType();
		const copyType = type === Box2D.b2_staticBody && this.sideMoves(side) ? Box2D.b2_kinematicBody : type;
		const state = this.mappedState(body, side.other, side.endpoint);
		const ghostBody = this.createBodyLike(body, copyType, state.position, state.angle, {
			gravityScale: 0,
		});
		const fixtureMap = new Map();
		for(const fixture of group.fixtures) {
			const copy = this.copyFixture(fixture, ghostBody, {shadow: group.shadow});
			if(!copy) continue;
			copy._portalGhostOf = fixture;
			fixtureMap.set(this.pointerOf(copy), fixture);
		}
		ghostBody.isPortalGhost = true;
		ghostBody.isPortalShadow = Boolean(group.shadow);
		ghostBody._portalGhostOf = body;
		ghostBody._portalGhostSide = side.endpoint;
		ghostBody._portalGhostOther = side.other;
		const ghost = {body, ghostBody, type, copyType, signature, shadow: Boolean(group.shadow), fixtureMap};
		this.syncGhostState(side, ghost, true);
		return ghost;
	}

	syncGhostState(side, ghost, poseChanged) {
		const {Box2D} = this;
		const {body, ghostBody} = ghost;
		const copyType = ghost.copyType ?? ghost.type;
		const dynamic = copyType !== Box2D.b2_staticBody;
		if(!dynamic && !poseChanged) return;
		const state = this.mappedState(body, side.other, side.endpoint);
		// A kinematic image of static geometry moves only with the mouths; its
		// pose is re-imposed when they move, its velocity kept current always.
		// Copies of bodies that move by themselves are re-imposed every step.
		if(ghost.type !== Box2D.b2_staticBody || poseChanged) {
			const position = new Box2D.b2Vec2(state.position.x, state.position.y);
			ghostBody.SetTransform(position, state.angle);
			Box2D.destroy(position);
		}
		if(!dynamic) return;
		const velocity = new Box2D.b2Vec2(state.linearVelocity.x, state.linearVelocity.y);
		ghostBody.SetLinearVelocity(velocity);
		ghostBody.SetAngularVelocity(state.angularVelocity);
		ghostBody.SetAwake(true);
		Box2D.destroy(velocity);
		if(ghost.type !== Box2D.b2_dynamicBody) return;
		// Box2D only runs continuous collision between two dynamic bodies when
		// one is a bullet, and a copy loses its share of any overlap correction
		// when it is re-synchronised, so a fast prop landing on an emerged part
		// would sink one step deep and climb out at half the usual rate. A fast,
		// free-standing prop's copy is therefore a bullet for the coming step.
		// At rest it must not be: the bullet position pass keeps nudging a
		// resting contact, and on an articulated island it disturbs the joints
		// of whatever it touches.
		ghostBody.SetBullet(Boolean(this.bulletCopies) && !ghost.shadow &&
			this.isNull(body.GetJointList()) &&
			Math.hypot(state.linearVelocity.x, state.linearVelocity.y) > BULLET_COPY_SPEED);
		// A copy must press on what it rests on with its real weight, pulled the
		// way gravity points in the room it stands for: a crate on a plank's
		// emerged half loads the real plank through the crate's copy. World
		// gravity is off for copies (a copy in mid-air would otherwise drift
		// from its mapped pose), so its mapped equivalent is applied as a force
		// that lasts exactly one step.
		const gravity = readVec(this.world.GetGravity());
		const mapped = transformPortalVector(
			gravity,
			side.other.body.GetAngle(),
			side.endpoint.body.GetAngle(),
		);
		const scale = ghostBody.GetMass() * body.GetGravityScale();
		if(!scale) return;
		const force = new Box2D.b2Vec2(mapped.x * scale, mapped.y * scale);
		ghostBody.ApplyForceToCenter(force, true);
		Box2D.destroy(force);
	}

	destroyGhost(ghost) {
		this.destroyOwnedBody(ghost.ghostBody);
	}

	destroySide(side) {
		this.destroySideJoints(side);
		for(const ghost of side.ghosts.values()) this.destroyGhost(ghost);
		side.ghosts.clear();
		this.sides.delete(side.endpoint);
	}

	endpointPose(side) {
		const source = side.endpoint.body;
		const target = side.other.body;
		const sourcePosition = source.GetPosition();
		const targetPosition = target.GetPosition();
		return [
			sourcePosition.get_x(), sourcePosition.get_y(), source.GetAngle(),
			targetPosition.get_x(), targetPosition.get_y(), target.GetAngle(),
		];
	}

	refreshSide(side, frame) {
		const transits = this.sideTransits(side);
		const shadowTransits = this.sideShadowTransits(side);
		if(!transits.length && !shadowTransits.length) {
			if(side.idleSinceFrame === null) side.idleSinceFrame = frame;
			if(frame - side.idleSinceFrame > SIDE_IDLE_FRAMES) {
				this.destroySide(side);
				return;
			}
		} else {
			side.idleSinceFrame = null;
		}
		if(!side.endpoint.body || side.endpoint.destroyed || !side.other.body || side.other.destroyed) {
			this.destroySide(side);
			return;
		}
		const pose = this.endpointPose(side);
		const poseChanged = !side.lastPose || pose.some((value, index) => value !== side.lastPose[index]);
		side.lastPose = pose;

		const region = transits.length || shadowTransits.length ?
			this.sideRegion(side, transits, shadowTransits) : null;
		const groups = region ? this.collectSideGeometry(side, region, shadowTransits) : new Map();

		const stale = [];
		for(const [key, ghost] of side.ghosts) {
			const group = groups.get(key);
			const wantedCopyType = group && group.body.GetType() === Box2D.b2_staticBody && this.sideMoves(side) ?
				Box2D.b2_kinematicBody : group?.body.GetType();
			if(!group || ghost.body.destroyed || ghost.ghostBody.destroyed ||
				this.ghostSignature(group) !== ghost.signature || ghost.type !== group.body.GetType() ||
				(ghost.copyType ?? ghost.type) !== wantedCopyType) {
				stale.push(key);
			}
		}
		// Destroying a ghost body destroys its joints natively; drop the joint
		// records first so no dangling joint is ever touched.
		if(stale.length) this.destroySideJoints(side);
		for(const key of stale) {
			this.destroyGhost(side.ghosts.get(key));
			side.ghosts.delete(key);
		}
		for(const [key, ghost] of side.ghosts) this.syncGhostState(side, ghost, poseChanged);
		for(const [key, group] of groups) {
			if(side.ghosts.has(key)) continue;
			side.ghosts.set(key, this.createGhost(side, group, this.ghostSignature(group)));
		}
		this.syncSideJoints(side);
	}

	// Recreate, in ghost space, every supported joint whose two bodies both
	// have ghosts on this side; keep motors, limits and springs in step.
	syncSideJoints(side) {
		const seen = new Set();
		for(const ghost of side.ghosts.values()) {
			const body = ghost.body;
			if(body.destroyed || body.GetType() !== this.Box2D.b2_dynamicBody) continue;
			for(let edge = body.GetJointList(); !this.isNull(edge); edge = edge.get_next()) {
				const joint = this.castJoint(edge.joint);
				const key = this.pointerOf(joint);
				if(seen.has(key)) continue;
				seen.add(key);
				const ghostA = side.ghosts.get(this.bodyKey(joint.GetBodyA()));
				const ghostB = side.ghosts.get(this.bodyKey(joint.GetBodyB()));
				if(!ghostA || !ghostB || ghostA.ghostBody.destroyed || ghostB.ghostBody.destroyed) continue;
				const signature = this.jointStructureSignature(joint);
				let record = side.joints.get(key);
				if(record && record.signature !== signature) {
					this.destroyGhostJoint(record);
					side.joints.delete(key);
					record = null;
				}
				if(!record) {
					const ghostJoint = this.createGhostJoint(side, joint, ghostA.ghostBody, ghostB.ghostBody);
					if(!ghostJoint) continue;
					record = {
						ghostJoint,
						signature,
						keyA: this.bodyKey(joint.GetBodyA()),
						keyB: this.bodyKey(joint.GetBodyB()),
						bodyA: ghostA.ghostBody,
						bodyB: ghostB.ghostBody,
					};
					side.joints.set(key, record);
				}
				this.syncJointState(joint, record.ghostJoint, side);
			}
		}
		for(const [key, record] of side.joints) {
			if(seen.has(key)) continue;
			this.destroyGhostJoint(record);
			side.joints.delete(key);
		}
	}

	destroySideJoints(side) {
		for(const record of side.joints.values()) this.destroyGhostJoint(record);
		side.joints.clear();
	}

	// Box2D frees a joint together with either of its bodies. The editor's
	// level clear destroys every body in the world, ghost bodies included,
	// before the portals reset, and a trigger may destroy a ghost body during
	// play; a joint whose ghost body is flagged destroyed is already gone and
	// destroying it again calls into freed memory.
	destroyGhostJoint(record) {
		const {ghostJoint, bodyA, bodyB} = record;
		if(!ghostJoint || ghostJoint.destroyed || bodyA?.destroyed || bodyB?.destroyed) return;
		ghostJoint.destroyed = true;
		this.world.DestroyJoint(ghostJoint);
	}

	createGhostJoint(side, sourceJoint, bodyA, bodyB) {
		const {Box2D} = this;
		const joint = this.castJoint(sourceJoint);
		const source = side.other;
		const target = side.endpoint;
		const mapPoint = point => this.mapPoint(readVec(point), source, target);
		const mapVector = vector => transformPortalVector(
			readVec(vector),
			source.body.GetAngle(),
			target.body.GetAngle(),
		);
		const anchorAData = mapPoint(joint.GetAnchorA());
		const anchorBData = mapPoint(joint.GetAnchorB());
		const anchorA = new Box2D.b2Vec2(anchorAData.x, anchorAData.y);
		const anchorB = new Box2D.b2Vec2(anchorBData.x, anchorBData.y);
		let definition = null;

		if(joint.GetType() === Box2D.e_revoluteJoint) {
			definition = new Box2D.b2RevoluteJointDef();
			definition.Initialize(bodyA, bodyB, anchorA);
			definition.set_referenceAngle(joint.GetReferenceAngle());
			definition.set_lowerAngle(joint.GetLowerLimit());
			definition.set_upperAngle(joint.GetUpperLimit());
			definition.set_maxMotorTorque(joint.GetMaxMotorTorque());
			definition.set_motorSpeed(joint.GetMotorSpeed());
			definition.set_enableLimit(joint.IsLimitEnabled());
			definition.set_enableMotor(joint.IsMotorEnabled());
		} else if(joint.GetType() === Box2D.e_prismaticJoint) {
			definition = new Box2D.b2PrismaticJointDef();
			const axisData = mapVector(joint.GetBodyA().GetWorldVector(joint.GetLocalAxisA()));
			const axis = new Box2D.b2Vec2(axisData.x, axisData.y);
			definition.Initialize(bodyA, bodyB, anchorA, axis);
			definition.set_referenceAngle(joint.GetReferenceAngle());
			definition.set_lowerTranslation(joint.GetLowerLimit());
			definition.set_upperTranslation(joint.GetUpperLimit());
			definition.set_maxMotorForce(joint.GetMaxMotorForce());
			definition.set_motorSpeed(joint.GetMotorSpeed());
			definition.set_enableLimit(joint.IsLimitEnabled());
			definition.set_enableMotor(joint.IsMotorEnabled());
			Box2D.destroy(axis);
		} else if(joint.GetType() === Box2D.e_distanceJoint) {
			definition = new Box2D.b2DistanceJointDef();
			definition.Initialize(bodyA, bodyB, anchorA, anchorB);
			definition.set_length(joint.GetLength());
			definition.set_minLength(joint.GetMinLength());
			definition.set_maxLength(joint.GetMaxLength());
			definition.set_stiffness(joint.GetStiffness());
			definition.set_damping(joint.GetDamping());
		} else if(joint.GetType() === Box2D.e_wheelJoint) {
			definition = new Box2D.b2WheelJointDef();
			const axisData = mapVector(joint.GetBodyA().GetWorldVector(joint.GetLocalAxisA()));
			const axis = new Box2D.b2Vec2(axisData.x, axisData.y);
			definition.Initialize(bodyA, bodyB, anchorA, axis);
			definition.set_stiffness(joint.GetStiffness());
			definition.set_damping(joint.GetDamping());
			definition.set_maxMotorTorque(joint.GetMaxMotorTorque());
			definition.set_motorSpeed(joint.GetMotorSpeed());
			definition.set_enableMotor(joint.IsMotorEnabled());
			definition.set_enableLimit(joint.IsLimitEnabled());
			definition.set_lowerTranslation(joint.GetLowerLimit());
			definition.set_upperTranslation(joint.GetUpperLimit());
			Box2D.destroy(axis);
		} else if(joint.GetType() === Box2D.e_weldJoint) {
			definition = new Box2D.b2WeldJointDef();
			definition.Initialize(bodyA, bodyB, anchorA);
			definition.set_referenceAngle(joint.GetReferenceAngle());
			definition.set_stiffness(joint.GetStiffness());
			definition.set_damping(joint.GetDamping());
		} else if(joint.GetType() === Box2D.e_frictionJoint) {
			definition = new Box2D.b2FrictionJointDef();
			definition.Initialize(bodyA, bodyB, anchorA);
			definition.set_maxForce(joint.GetMaxForce());
			definition.set_maxTorque(joint.GetMaxTorque());
		} else if(joint.GetType() === Box2D.e_motorJoint) {
			definition = new Box2D.b2MotorJointDef();
			definition.Initialize(bodyA, bodyB);
			definition.set_linearOffset(joint.GetLinearOffset());
			definition.set_angularOffset(joint.GetAngularOffset());
			definition.set_maxForce(joint.GetMaxForce());
			definition.set_maxTorque(joint.GetMaxTorque());
			definition.set_correctionFactor(joint.GetCorrectionFactor());
		} else if(joint.GetType() === Box2D.e_mouseJoint) {
			// The Animator prefab drives a moving platform with a mouse joint. Its
			// copy needs the same spring toward the mapped target, or a traveller
			// standing on the copy while it straddles the seam would push a free
			// body into empty space.
			definition = new Box2D.b2MouseJointDef();
			definition.set_bodyA(bodyA);
			definition.set_bodyB(bodyB);
			const targetData = mapPoint(joint.GetTarget());
			const target = new Box2D.b2Vec2(targetData.x, targetData.y);
			definition.set_target(target);
			definition.set_maxForce(joint.GetMaxForce());
			definition.set_stiffness(joint.GetStiffness());
			definition.set_damping(joint.GetDamping());
			Box2D.destroy(target);
		}

		Box2D.destroy(anchorB);
		Box2D.destroy(anchorA);
		if(!definition) return null;
		// Initialize uses a single world anchor for several joint types. The
		// mapping is rigid, so the original local anchors are exact in ghost space.
		if(typeof definition.set_localAnchorA === 'function' &&
			typeof joint.GetLocalAnchorA === 'function') {
			definition.set_localAnchorA(joint.GetLocalAnchorA());
			definition.set_localAnchorB(joint.GetLocalAnchorB());
		}
		definition.set_collideConnected(joint.GetCollideConnected());
		const copy = this.world.CreateJoint(definition);
		Box2D.destroy(definition);
		return copy;
	}

	syncJointState(sourceJoint, targetJoint, side = null) {
		const {Box2D} = this;
		const source = this.castJoint(sourceJoint);
		const target = this.castJoint(targetJoint);
		if(source.GetType() !== target.GetType()) return;

		if(source.GetType() === Box2D.e_mouseJoint) {
			if(side) {
				const mapped = this.mapPoint(readVec(source.GetTarget()), side.other, side.endpoint);
				const point = new Box2D.b2Vec2(mapped.x, mapped.y);
				target.SetTarget(point);
				Box2D.destroy(point);
			}
			target.SetMaxForce(source.GetMaxForce());
			target.SetStiffness(source.GetStiffness());
			target.SetDamping(source.GetDamping());
			return;
		}

		if(source.GetType() === Box2D.e_revoluteJoint) {
			target.SetLimits(source.GetLowerLimit(), source.GetUpperLimit());
			target.SetMaxMotorTorque(source.GetMaxMotorTorque());
			target.SetMotorSpeed(source.GetMotorSpeed());
			target.EnableLimit(source.IsLimitEnabled());
			target.EnableMotor(source.IsMotorEnabled());
		} else if(source.GetType() === Box2D.e_prismaticJoint) {
			target.SetLimits(source.GetLowerLimit(), source.GetUpperLimit());
			target.SetMaxMotorForce(source.GetMaxMotorForce());
			target.SetMotorSpeed(source.GetMotorSpeed());
			target.EnableLimit(source.IsLimitEnabled());
			target.EnableMotor(source.IsMotorEnabled());
		} else if(source.GetType() === Box2D.e_distanceJoint) {
			target.SetLength(source.GetLength());
			target.SetMinLength(source.GetMinLength());
			target.SetMaxLength(source.GetMaxLength());
			target.SetStiffness(source.GetStiffness());
			target.SetDamping(source.GetDamping());
		} else if(source.GetType() === Box2D.e_wheelJoint) {
			target.SetStiffness(source.GetStiffness());
			target.SetDamping(source.GetDamping());
			target.SetMaxMotorTorque(source.GetMaxMotorTorque());
			target.SetMotorSpeed(source.GetMotorSpeed());
			target.EnableMotor(source.IsMotorEnabled());
			target.EnableLimit(source.IsLimitEnabled());
			target.SetLimits(source.GetLowerLimit(), source.GetUpperLimit());
		} else if(source.GetType() === Box2D.e_weldJoint) {
			target.SetStiffness(source.GetStiffness());
			target.SetDamping(source.GetDamping());
		} else if(source.GetType() === Box2D.e_frictionJoint) {
			target.SetMaxForce(source.GetMaxForce());
			target.SetMaxTorque(source.GetMaxTorque());
		} else if(source.GetType() === Box2D.e_motorJoint) {
			target.SetLinearOffset(source.GetLinearOffset());
			target.SetAngularOffset(source.GetAngularOffset());
			target.SetMaxForce(source.GetMaxForce());
			target.SetMaxTorque(source.GetMaxTorque());
			target.SetCorrectionFactor(source.GetCorrectionFactor());
		}
	}

	// Bring ghosts and shadows up to date for the next step. Runs after the
	// primary step, when the world is unlocked.
	sync(frame) {
		if(this.destroyed) return;
		for(const candidate of this.transits) {
			this.refreshTravellerTags(candidate);
			this.ensureSide(candidate.source, candidate.target, frame);
			this.ensureSide(candidate.target, candidate.source, frame);
		}
		for(const side of [...this.sides.values()]) this.refreshSide(side, frame);
		for(const candidate of this.transits) this.collectShadows(candidate);
		this.flushRetiredBodies();
	}

	// The island's shadow bodies at its exit, by real body key: what the transit
	// visual, intersection particles and portal audio read.
	collectShadows(candidate) {
		const side = this.sides.get(candidate.target);
		candidate.bodyMap.clear();
		let live = 0;
		for(const body of candidate.bodies) {
			if(body.destroyed) continue;
			live++;
			const ghost = side?.ghosts.get(this.bodyKey(body));
			if(ghost && ghost.shadow && !ghost.ghostBody.destroyed) {
				candidate.bodyMap.set(this.bodyKey(body), ghost.ghostBody);
			}
		}
		candidate.shadowsPending = candidate.bodyMap.size < live;
	}

	ghostCount() {
		let count = 0;
		for(const side of this.sides.values()) count += side.ghosts.size;
		return count;
	}

	destroy() {
		if(this.destroyed) return;
		this.destroyed = true;
		for(const candidate of [...this.transits]) this.removeTransit(candidate);
		for(const side of [...this.sides.values()]) this.destroySide(side);
		this.flushRetiredBodies();
		this.Box2D.destroy(this.queryAABB);
		this.Box2D.destroy(this.queryCallback);
		this.queryAABB = null;
		this.queryCallback = null;
	}
}
